// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierShopModels.jsx   ·   A WING'S OWN BUILDING, FROM BLENDER
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-24: "I want every building to be completely unique and
// have a real true personality — Snake Eyes should look like a Casino …
// Snake Casino Terror style … Annihilation: skulls, rib cages, bones, a
// fleshy outside, entering a rib cage opening with a ram skull on top."
//
// THE SEAM. One row per wing in SHOP_MODELS → `public/models/shop_<id>.glb`,
// built by gamemaster/models/sculpt/builders/pier_shop_blender/ (one
// builder file per wing: shops/<id>.py). The code <Shopfront/> is the
// fail-safe: no row, not live, the glb missing/failing, or a glb built for
// a different frontage WIDTH → the code shop stands, exactly as today.
//
// ⚑ THE CONTRACT A SHOP GLB MUST KEEP (the code Shopfront's own frame):
//   · origin = the middle of the frontage, ON the edge line, at deck level
//   · +z (glTF) is the STREET; the building runs back into −z
//   · it stays inside its frontage: |x| ≤ w/2, and 0 ≥ z ≥ −depth
//   · the root carries extras `ie_shop` = { id, w, v, door:[x,y,w,h], mass:[x0,y0,z0,x1,y1,z1] }
//     `door` is where the game hangs the LIVE portal (the wing's own rift,
//     tinted by GAME_PORTALS.color, turning — it IS the door you walk into);
//     `mass` is the box the camera fader tests (ends at the wall, z ≤ 0)
//   · meshes named [<id>_]glow_* are lit (their own colour as emission, never tone-mapped)
// Everything else in the glb is the wing's to be as wild as it likes.
//
// ⚑ LIVE SWITCH: a row shows on the pier only when `live: true`, so a
// building in progress never lands by accident. `?shops=blender` shows
// every row that has a glb (review), `?shops=code` shows none (a way out).
// ════════════════════════════════════════════════════════════════════
import { Component, Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { Shopfront, BUILD_TICKS, useOccluder } from "./pierBuild";
import { riftTex, poolTex } from "./pierPaint";

// ⭐ ONE ROW PER WING. `live` flips it on for everyone.
export const SHOP_MODELS = {
  // Snake Eyes — the Snake Casino. ⚠ shops/casino.py is still the SCAFFOLD (a
  // placeholder mass proving the seam), so NOT live: see it with ?shops=blender
  casino: { live: true },   // 2026-10-24: Clams Casino clam on the roof (caP'n: put it on the pier). Body is still the scaffold mass.
  // terror: { live: false },
  // Annihilation — the rib-cage maw under V'ael's ram skull (shops/annihilation.py). LIVE 2026-09-25
  annihilation: { live: true },
  // Hooked — the outfitter's lodge (Bass Pro-style) + a fishing pier out to sea on its
  // left, Hooked's own skiff hanging in twin davits (shops/hooked.py). LIVE 2026-09-25.
  // ⚑ Its glb reaches PAST its frontage on purpose (the pier, over open sea behind the
  // court's back rail — never the walk); `mass` stays the lodge's box.
  hooked: { live: true },
  // Moonshine Masters — the hooch shack: tin roof, a crooked loft, a crescent moon cut in the
  // door, a copper pot still bubbling on the porch (shops/moonshine.py). LIVE 2026-10-24.
  moonshine: { live: true },
};

const Q = typeof window !== "undefined" ? window.location.search : "";
const MODE = /[?&]shops=blender\b/.test(Q) ? "blender" : /[?&]shops=code\b/.test(Q) ? "code" : "live";
export const shopUrl = (id) => `${process.env.PUBLIC_URL || ""}/models/shop_${id}.glb`;
export function shopRowFor(portal) {
  const row = SHOP_MODELS[portal.id];
  if (!row || MODE === "code") return null;
  if (MODE === "blender" || row.live) return row;
  return null;
}

function readShop(scene) {
  let s = null;
  scene.traverse((o) => { if (!s && o.userData && o.userData.ie_shop) { try { s = JSON.parse(o.userData.ie_shop); } catch (e) { s = null; } } });
  return s;
}

function ShopModel({ portal }) {
  const { store } = portal;
  const soon = portal.status === "coming_soon";
  const gltf = useLoader(GLTFLoader, shopUrl(portal.id), (l) => l.setMeshoptDecoder(MeshoptDecoder));
  const meta = useMemo(() => readShop(gltf.scene), [gltf]);
  const fits = !!meta && meta.id === portal.id && Math.abs(meta.w - store.w) < 0.01;

  const built = useMemo(() => {
    if (!fits) return null;
    const root = gltf.scene.clone(true);
    const mats = new Set();
    root.traverse((o) => {
      if (!o.isMesh) return;
      const m = Array.isArray(o.material) ? o.material : [o.material];
      o.material = m.map((mm) => {
        const c = mm.clone();
        if (/(?:^|_)glow_/.test(o.name) || /(?:^|_)glow_/.test((o.parent && o.parent.name) || "")) { c.toneMapped = false; c.emissive && c.emissive.copy(c.color); }
        mats.add(c); return c;
      });
      if (o.material.length === 1) o.material = o.material[0];
    });
    const rift = new THREE.MeshBasicMaterial({
      map: riftTex(portal.color), transparent: true, depthWrite: false, toneMapped: false,
      blending: THREE.AdditiveBlending, opacity: soon ? 0.35 : 0.95,
    });
    const spill = new THREE.MeshBasicMaterial({
      map: poolTex(), color: portal.color, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, opacity: soon ? 0.22 : 0.6,
    });
    return { root, mats: [...mats], rift, spill };
  }, [gltf, fits, portal.color, soon]);

  useEffect(() => () => { if (built) { built.mats.forEach((m) => m.dispose()); built.rift.dispose(); built.spill.dispose(); } }, [built]);

  const ref = useRef();
  const mass = useMemo(() => {
    const b = meta && meta.mass;
    return b ? new THREE.Box3(new THREE.Vector3(b[0], b[1], b[2]), new THREE.Vector3(b[3], b[4], Math.min(b[5], -0.02)))
      : new THREE.Box3(new THREE.Vector3(-store.w / 2, 0, -3), new THREE.Vector3(store.w / 2, 4, -0.02));
  }, [meta, store.w]);
  const occ = useOccluder(ref, mass, built ? built.mats : [], [built, mass]);

  // the door turns like every other wing's, and fades with its building
  useEffect(() => {
    if (!built) return undefined;
    const fn = (t) => {
      const map = built.rift.map; map.center.set(0.5, 0.5); map.rotation = t * (soon ? 0.12 : 0.45);
      const k = occ.current ? occ.current.k : 1;
      built.rift.opacity = (soon ? 0.35 : 0.95) * k; built.spill.opacity = (soon ? 0.22 : 0.6) * k;
    };
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
  }, [built, soon, occ]);

  if (!fits) {
    console.warn("[pier] shop_" + portal.id + ".glb was built for", meta, "but the frontage is", store.w, "wide — the code shop stands in");
    return <Shopfront portal={portal} />;
  }
  const rotY = { S: 0, N: Math.PI, E: Math.PI / 2, W: -Math.PI / 2 }[store.face] ?? 0;
  const onNS = store.face === "S" || store.face === "N";
  const placeAt = onNS ? [store.x + store.w / 2, 0, store.y] : [store.x, 0, store.y + store.w / 2];
  const [dx, dy, dw, dh] = meta.door || [0, 0.14, 1.0, 1.72];
  return (
    <group ref={ref} position={placeAt} rotation={[0, rotY, 0]}>
      <primitive object={built.root} />
      <mesh position={[dx, dy + dh / 2, 0.02]} material={built.rift}><planeGeometry args={[dw, dh]} /></mesh>
      <mesh position={[dx, 0.02, 0.95]} rotation={[-Math.PI / 2, 0, 0]} material={built.spill}><planeGeometry args={[2.6, 1.9]} /></mesh>
    </group>
  );
}

class ShopBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[pier] a shop glb did not load — the code shop stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// ⭐ What Pier3D renders for every wing.
export function ShopSlot({ portal }) {
  const code = <Shopfront portal={portal} />;
  if (!shopRowFor(portal)) return code;
  return (
    <ShopBoundary fallback={code}>
      <Suspense fallback={code}><ShopModel portal={portal} /></Suspense>
    </ShopBoundary>
  );
}
export default ShopSlot;
